// src/store/useChecklistStore.ts
import { create }  from 'zustand'
import { persist } from 'zustand/middleware'
import axios       from 'axios'
import { getStoreAuthHeaders } from '../utils/auth'
import { BACKEND } from '../utils/config'

// ── Types ──────────────────────────────────────────────────────────────────────
export interface ChecklistItem {
  id:             string
  label:          string
  icon:           string
  question:       string
  category_codes: string[] // MRC codes like ['03F', 'AE']
  category_codes_str?: string // temporary helper for UI
  is_base:        boolean  // Always shown if true
  order:          number
}

interface ChecklistState {
  checklistItems:   ChecklistItem[]
  itemsLoaded:      boolean
  itemsLoading:     boolean
  itemsError:       string | null

  fetchItems:   ()                                              => Promise<void>
  addItem:      (item: Omit<ChecklistItem, 'order'>)           => Promise<void>
  updateItem:   (id: string, patch: Partial<Omit<ChecklistItem, 'id'>>) => Promise<void>
  removeItem:   (id: string)                                   => Promise<void>
  reorderItems: (ordered: ChecklistItem[])                     => Promise<void>

  _authHeader:  () => Record<string, string>
}

// ── Store ──────────────────────────────────────────────────────────────────────
export const useChecklistStore = create<ChecklistState>()(
  persist(
    (set, get) => ({

      checklistItems:   [],
      itemsLoaded:      false,
      itemsLoading:     false,
      itemsError:       null,

      // ── Auth header — via store Zustand (plus de localStorage hack) ────────
      _authHeader: (): Record<string, string> => {
        try {
          return getStoreAuthHeaders()
        } catch { return {} }
      },

      // ── GET /api/config/checklist ─────────────────────────────────────────────
      fetchItems: async () => {
        set({ itemsLoading: true, itemsError: null })
        try {
          const { data } = await axios.get<ChecklistItem[]>(
            `${BACKEND}/api/config/checklist`,
            { headers: get()._authHeader() }
          )
          const sorted = [...data].sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
          set({ checklistItems: sorted, itemsLoaded: true, itemsLoading: false })
        } catch (err: any) {
          console.warn('[fetchItems]', err.message)
          set({ itemsLoaded: true, itemsLoading: false, itemsError: err.message })
        }
      },

      // ── POST /api/config/checklist ────────────────────────────────────────────
      addItem: async (item) => {
        const newItem: ChecklistItem = {
          ...item,
          order: get().checklistItems.length,
        }
        const { data } = await axios.post<ChecklistItem>(
          `${BACKEND}/api/config/checklist`,
          newItem,
          { headers: { 'Content-Type': 'application/json', ...get()._authHeader() } }
        )
        const updated = [...get().checklistItems, data]
        set({ checklistItems: updated })
      },

      // ── PUT /api/config/checklist/:id ─────────────────────────────────────────
      updateItem: async (id, patch) => {
        await axios.put(
          `${BACKEND}/api/config/checklist/${id}`,
          patch,
          { headers: { 'Content-Type': 'application/json', ...get()._authHeader() } }
        )
        const updated = get().checklistItems.map(s => s.id === id ? { ...s, ...patch } : s)
        set({ checklistItems: updated })
      },

      // ── DELETE /api/config/checklist/:id ──────────────────────────────────────
      removeItem: async (id) => {
        await axios.delete(
          `${BACKEND}/api/config/checklist/${id}`,
          { headers: get()._authHeader() }
        )
        const updated = get().checklistItems
          .filter(s => s.id !== id)
          .map((s, i) => ({ ...s, order: i }))
        set({ checklistItems: updated })
      },

      // ── POST /api/config/checklist/reorder ────────────────────────────────────
      reorderItems: async (ordered) => {
        const reindexed = ordered.map((s, i) => ({ ...s, order: i }))
        // Optimiste
        set({ checklistItems: reindexed })
        await axios.post(
          `${BACKEND}/api/config/checklist/reorder`,
          { items: reindexed },
          { headers: { 'Content-Type': 'application/json', ...get()._authHeader() } }
        )
      },

    }),
    {
      name:       'rentalcheck-checklist-v1',
      partialize: (state) => ({
        checklistItems:  state.checklistItems,
        itemsLoaded:     state.itemsLoaded,
      }),
    }
  )
)
